<template>
  <vs-wrapper space="small">
    <v-row dense align="center">
      <v-col>
        <vs-text type="caption"> Job #{{ item.id }} · {{ company?.shortCode }} </vs-text>
      </v-col>
      <v-col class="shrink">
        <v-icon :color="item.priority.color">
          {{ item.priority.icon }}
        </v-icon>
      </v-col>
      <v-col class="shrink">
        <job-status-chip :status="finalStatus(item)" />
      </v-col>
    </v-row>
    <vs-heading>{{ item.activity?.taskName }}</vs-heading>
    <vs-text v-if="item.details" type="caption" data-test="job-details">{{
      formatDetails
    }}</vs-text>
    <v-row data-test="planned-time" align="center">
      <v-col class="shrink">
        <v-icon> date_range </v-icon>
      </v-col>
      <v-col class="pl-4">
        <vs-text type="caption">Planned</vs-text>
        <vs-text>{{ formatPlannedTime }}</vs-text>
      </v-col>
    </v-row>

    <v-row v-if="showActualTimes" data-test="actual-time" align="center">
      <v-col class="shrink">
        <v-icon> date_range </v-icon>
      </v-col>
      <v-col class="pl-4">
        <vs-text type="caption">Actual</vs-text>
        <vs-text>{{ formatActualTime }}</vs-text>
      </v-col>
    </v-row>

    <v-row v-if="showActiveDuration" align="center" data-test="job-duration">
      <v-col class="shrink">
        <v-icon> timer</v-icon>
      </v-col>
      <v-col class="pl-4">
        <vs-text>Active for {{ formatHoursForActiveDuration }}</vs-text>
        <vs-text v-if="showPausedDuration">Paused for {{ formatHoursForPausedDuration }} </vs-text>
      </v-col>
    </v-row>

    <job-card-locations-detail
      :inventory="inventory"
      :destination-location-name="item.destinationLocation"
    />

    <v-row align="center">
      <v-col class="shrink">
        <v-icon> person </v-icon>
      </v-col>
      <v-col class="pl-4">
        <vs-text>Requested by {{ customerName }}</vs-text>
      </v-col>
    </v-row>
  </vs-wrapper>
</template>

<script setup lang="ts">
import { formatDateTime, formatDurationAsHours, formatTimeRange } from '@/utils/date-formatting.js'
import JobStatusChip from '@/components/tickets/JobStatusChip.vue'
import { NO_APPROVAL } from '@/models/job'
import JobCardLocationsDetail from '../jobs/JobCardLocationsDetail.vue'
import moment from 'moment'
import { computed } from 'vue'
import { Item } from '@/components/weekly-schedule/utils'
import { useStore } from 'vuex-composition-helpers'
import { JobStopType } from '@ironsight/types'

const props = defineProps<{ item: Item }>()
const store = useStore()

const inventory = computed(() =>
  props.item.requestedStops
    .filter((s) => s.stopType === JobStopType.PickUp)
    .flatMap((stop) =>
      stop.inventory.map((inv) => ({
        inventoryId: inv.id,
        inventorySubTypeId: inv.inventorySubTypeId,
        inventorySubTypeName: inv.inventorySubTypeName,
        inventoryTypeId: inv.inventoryTypeId,
        name: inv.inventoryTypeName,
        pickupLocationId: stop.locationId,
        pickupLocationName: stop.locationName,
        pickupLocationOwnerId: stop.locationOwnerId,
        quantity: inv.quantity,
        position: stop.position,
      })),
    ),
)

const company = computed(() => store.getters['companies/getByChildHubId'](props.item?.hubId))
const formatDetails = computed(() => {
  if (props.item.details.length > 150) {
    return props.item.details.slice(0, 150) + '...'
  }
  return props.item.details
})
const customerName = computed(() => props.item.customer && props.item.customer.name)
const showActualTimes = computed(() => props.item.activeTime || props.item.completedTime)
const showActiveDuration = computed(
  () => props.item.actualActiveDuration >= moment.duration(0.01, 'hours'),
)
const showPausedDuration = computed(
  () => props.item.pausedDuration >= moment.duration(0.01, 'hours'),
)
const formatPlannedTime = computed(() => formatTimeRange(props.item.startTime, props.item.endTime))
const formatActualTime = computed(() => {
  if (!props.item.completedTime) {
    return formatTimeRange(props.item.activeTime, props.item.completedTime, '-', 'Ongoing')
  }

  if (!props.item.activeTime) {
    return `${formatDateTime(props.item.completedTime)}`
  }

  const activeMinute = moment(props.item.activeTime).startOf('minute')
  const completedMinute = moment(props.item.completedTime).startOf('minute')

  if (activeMinute.isSame(completedMinute)) {
    return `${formatDateTime(props.item.completedTime)}`
  }

  return formatTimeRange(props.item.activeTime, props.item.completedTime)
})
const formatHoursForActiveDuration = computed(() =>
  formatDurationAsHours(props.item.actualActiveDuration, true),
)
const formatHoursForPausedDuration = computed(() =>
  formatDurationAsHours(props.item.pausedDuration, false),
)
const finalStatus = (job: Item) =>
  job.approvalStatus !== NO_APPROVAL ? job.approvalStatus : job.status
</script>
